import React, { useCallback } from 'react';

import { type Collectible, OrderStatus, OrderType, Side } from '@pkg/graphql/codegen/graphql';
import { useAppSelector, useAuthentication, useBreakpoints, useNewOrder } from '@pkg/hooks';

import { useNFTsPageStore, useOrdersStore } from '@app/store/zustand';

import { CurrencyIcon } from './CurrencyIcon';
import { MarketplaceButton } from './MarketplaceButton';
import { NFTManagerControls } from './NFTManagerControls';
import { SelectedButton } from './SelectedButton';

import { Wrapper } from './styles';

interface CardMarketplaceControlsProps {
    item: Collectible;
    isHovering?: boolean;
    isOwner?: boolean;
    isPreview?: boolean;
}

export const CardMarketplaceControls = ({ item, isOwner, isHovering, isPreview }: CardMarketplaceControlsProps) => {
    const id = item?.id || '';
    const { orderRequestPendingId } = useOrdersStore();

    const { protectedAction } = useAuthentication();
    const marketplaceV1Enabled = useAppSelector((state) => state.featureFlags.features.marketplaceV1Enabled);
    const { selectedItems } = useNFTsPageStore();
    const { isMobile } = useBreakpoints();

    const { buy, offer } = useNewOrder();

    const isSelected = selectedItems.includes(item.id || '');
    //TODO: make util/helper
    const viableListing = item?.orders?.find(
        (order) =>
            order.id?.startsWith('yat') &&
            order.side === Side.Ask &&
            order.type === OrderType.Limit &&
            Date.parse(order.expiresAt) > Date.now() &&
            (order.status === OrderStatus.Pending || order.status === OrderStatus.New),
    );

    const isListed = !!viableListing;

    const onMarketplaceClick = useCallback(() => {
        return protectedAction(() => {
            if (isListed) {
                return buy([item]);
            } else {
                return offer([item]);
            }
        });
    }, [buy, isListed, item, offer, protectedAction]);

    const marketplaceCTA = marketplaceV1Enabled && (
        <MarketplaceButton
            isListed={isListed}
            onClick={onMarketplaceClick}
            disabled={isPreview}
            isLoading={Boolean(orderRequestPendingId?.length && orderRequestPendingId === id)}
        />
    );

    // TODO: This is supposed to be of type `SupportedChain`.
    const [itemChain] = id.split('.');
    const validChain = itemChain === 'ethereum' || itemChain === 'aptos' || itemChain === 'polygon';

    return (
        <Wrapper $isHovering={isHovering}>
            {(isHovering || isMobile) && validChain ? <CurrencyIcon chain={itemChain} /> : null}

            {(isHovering || isSelected || isMobile) && marketplaceV1Enabled && isOwner && (
                <SelectedButton item_id={item.id} isSelected={isSelected} />
            )}
            {!isOwner ? (
                marketplaceCTA
            ) : (
                <NFTManagerControls item={item} viableListing={viableListing} isListed={isListed} />
            )}
        </Wrapper>
    );
};
